import React, { useState, useEffect } from 'react';
import { useQuery } from 'react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { inventoryService } from '../../../services/inventoryService';
import LoadingSpinner from '../../../components/LoadingSpinner';
import ErrorAlert from '../../../components/ErrorAlert';
import InventoryStylePagination from '../../../components/InventoryStylePagination';
import { FiPlus, FiSearch } from 'react-icons/fi';
import { LuEye, LuSquarePen } from 'react-icons/lu';

const InventoryList = () => {
  const [searchParams] = useSearchParams();
  const initialCompanyId = searchParams.get('companyId') || '';
  const [searchTerm, setSearchTerm] = useState('');
  const [stockFilter, setStockFilter] = useState('all');
  const [customerFilter, setCustomerFilter] = useState(initialCompanyId);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  
  // Fetch customers for the filter dropdown
  const { data: customersData } = useQuery(
    ['customers'],
    () => inventoryService.getCustomers(),
    { 
      staleTime: 300000, // 5 minutes
      refetchOnWindowFocus: false 
    }
  );
  
  // Fetch inventory data
  const { data, isLoading, error, isPreviousData } = useQuery(
    ['inventory', searchTerm, stockFilter, customerFilter, page, limit],
    () => inventoryService.getInventory(
      page,
      limit,
      customerFilter !== '' ? customerFilter : undefined,
      searchTerm
    ),
    { 
      keepPreviousData: true,
      staleTime: 15000, // 15 seconds
    }
  );
  
  // Reset to first page when filters change
  useEffect(() => {
    setPage(1);
  }, [searchTerm, stockFilter, customerFilter]);
  
  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };
  
  const handleStockFilterChange = (e) => {
    setStockFilter(e.target.value);
  };
  
  const handleCustomerFilterChange = (e) => {
    setCustomerFilter(e.target.value);
  };
  
  const getStockStatus = (quantity, reorderThreshold) => {
    if (quantity <= 0) return 'Out of Stock';
    if (reorderThreshold && quantity <= reorderThreshold) return 'Low Stock';
    return 'In Stock';
  };
  
  // Filter inventory items based on stock status
  const filteredItems = data?.data?.filter(item => {
    if (!item) return false;
    
    const status = getStockStatus(item.quantity, item.reorderThreshold);
    
    return stockFilter === 'all' || 
           (stockFilter === 'low-stock' && status === 'Low Stock') ||
           (stockFilter === 'in-stock' && status === 'In Stock') ||
           (stockFilter === 'out-of-stock' && status === 'Out of Stock');
  }) || [];
  
  if (isLoading && !isPreviousData) return <LoadingSpinner />;
  if (error) return <ErrorAlert message={`Error loading inventory data: ${error.message}`} />;
  
  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8 w-full">
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-xl font-semibold text-gray-900">Inventory</h1>
          <p className="mt-2 text-sm text-gray-700">
            Manage warehouse inventory items, stock levels, and locations.
          </p>
        </div>
        <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
          <Link
            to="/admin/inventory/new"
            className="inline-flex items-center justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:w-auto"
          >
            <FiPlus className="mr-2" /> Add Item
          </Link>
        </div>
      </div>

      <div className="mt-6 mb-8">
        <div className="mb-4">
          <div className="relative rounded-md shadow-sm max-w-md">
            <input
              type="text"
              placeholder="Search by SKU, name, serial..."
              className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pr-10 sm:text-sm border-gray-300 rounded-md"
              value={searchTerm}
              onChange={handleSearch}
            />
            <div className="absolute inset-y-0 right-0 flex items-center pr-3">
              <FiSearch className="h-4 w-4 text-gray-400" />
            </div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-3 sm:gap-4">
          <div className="flex items-center">
            <select
              value={customerFilter}
              onChange={handleCustomerFilterChange}
              className="focus:ring-indigo-500 focus:border-indigo-500 rounded-md border-gray-300 py-2 pl-3 pr-10 text-sm w-full sm:w-auto"
            >
              <option value="">All Customers</option>
              {customersData?.customers?.map(customer => (
                <option key={customer.id} value={customer.id}>
                  {customer.companyName}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-center">
            <select
              value={stockFilter}
              onChange={handleStockFilterChange}
              className="focus:ring-indigo-500 focus:border-indigo-500 rounded-md border-gray-300 py-2 pl-3 pr-10 text-sm w-full sm:w-auto"
            >
              <option value="all">All Items</option>
              <option value="in-stock">Available</option>
              <option value="low-stock">Low Available</option>
              <option value="out-of-stock">No Available Stock</option>
            </select>
          </div>
        </div>
        <p className="mt-3 text-xs text-gray-500">
          Available-stock filters use current in-stock quantity only. Pending receive, pending shipment, and held units are shown on item detail.
        </p>
      </div>
      
      {/* Inventory Table */}
      <div className="bg-white shadow overflow-hidden rounded-lg">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  SKU / Name
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Customer
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Available Quantity
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {isLoading && !data ? (
                <tr>
                  <td colSpan="7" className="px-6 py-4 text-center">
                    <LoadingSpinner />
                  </td>
                </tr>
              ) : filteredItems?.length > 0 ? (
                filteredItems.map((item) => {
                  const status = getStockStatus(item.quantity, item.reorderThreshold);
                  return (
                    <tr key={item.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div>
                            <div className="text-sm font-medium">
                              <Link to={`/admin/inventory/${item.id}`} className="text-indigo-600 hover:text-indigo-900">
                                {item.productSku}
                              </Link>
                            </div>
                            <div className="text-sm text-gray-500">
                              {item.productName}
                            </div>
                            {item.serialNumber && (
                              <div className="text-xs text-gray-400">
                                SN: {item.serialNumber}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{item.Customer?.companyName}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{item.quantity}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-3">
                          <Link to={`/admin/inventory/${item.id}`} className="text-gray-400 hover:text-gray-600" aria-label="View">
                            <LuEye className="h-4 w-4" />
                          </Link>
                          <Link to={`/admin/inventory/${item.id}/edit`} className="text-gray-400 hover:text-gray-600" aria-label="Edit">
                            <LuSquarePen className="h-4 w-4" />
                          </Link>
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="7" className="px-6 py-4 text-center text-gray-500">
                    No inventory items found matching your criteria.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        
        <InventoryStylePagination
          currentPage={page}
          totalPages={data?.pages || 1}
          totalItems={data?.total || 0}
          pageSize={limit}
          onPageChange={setPage}
          onPageSizeChange={(nextLimit) => {
            setLimit(nextLimit);
            setPage(1);
          }}
        />
      </div>
    </div>
  );
};

export default InventoryList; 
